import React, { useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface FeeRecord {
  id: string;
  term: string;
  amount: number;
  paid: number;
  due: number;
  dueDate: string;
  status: 'paid' | 'pending' | 'overdue';
}

interface StudentFeesLookupProps {
  domain: string;
}

const StudentFeesLookup: React.FC<StudentFeesLookupProps> = ({ domain }) => {
  const [studentId, setStudentId] = useState('');
  const [fees, setFees] = useState<FeeRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [studentName, setStudentName] = useState('');

  const loadFees = async () => {
    if (!studentId) {
      alert('Please enter a Student ID');
      return;
    }

    setLoading(true);
    try {
      // Load student info first
      const studentRef = collection(db, `school-users/${domain}/students`);
      const studentSnapshot = await getDocs(studentRef);
      let foundStudent = false;

      studentSnapshot.forEach((doc) => {
        if (doc.id === studentId || doc.data().studentId === studentId) {
          setStudentName(doc.data().name || 'Unknown Student');
          foundStudent = true;
        }
      });

      if (!foundStudent) {
        alert('Student not found');
        setFees([]);
        setLoading(false);
        return;
      }

      // Load fees data
      const feesRef = collection(db, `school-users/${domain}/students/${studentId}/fees`);
      const snapshot = await getDocs(feesRef);

      const feesList: FeeRecord[] = [];
      snapshot.forEach((doc) => {
        feesList.push({ id: doc.id, ...doc.data() } as FeeRecord);
      });

      setFees(feesList.sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()));
    } catch (error) {
      console.error('Error loading fees:', error);
      alert('Failed to load fees data');
    } finally {
      setLoading(false);
    }
  };

  const getTotalStats = () => {
    const totalAmount = fees.reduce((sum, fee) => sum + fee.amount, 0);
    const totalPaid = fees.reduce((sum, fee) => sum + fee.paid, 0);
    const totalDue = fees.reduce((sum, fee) => sum + fee.due, 0);

    return { totalAmount, totalPaid, totalDue };
  };

  const stats = getTotalStats();

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-white py-3">
        <h5 className="mb-0">
          <i className="bi bi-search me-2 text-primary-custom"></i>
          Student Fees Lookup
        </h5>
        <small className="text-muted">Search and view student fee details</small>
      </div>
      <div className="card-body">
        {/* Search Section */}
        <div className="row mb-4">
          <div className="col-md-8">
            <label className="form-label">Student ID *</label>
            <div className="input-group">
              <span className="input-group-text">
                <i className="bi bi-person-badge"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Enter Student ID (e.g., 2024001)"
                value={studentId}
                onChange={(e) => setStudentId(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') loadFees();
                }}
              />
              <button
                className="btn btn-primary"
                onClick={loadFees}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Loading...
                  </>
                ) : (
                  <>
                    <i className="bi bi-search me-2"></i>
                    Search
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Student Info */}
        {studentName && (
          <div className="alert alert-info mb-4">
            <i className="bi bi-person-circle me-2"></i>
            <strong>Student:</strong> {studentName} | <strong>ID:</strong> {studentId}
          </div>
        )}

        {/* Fee Statistics */}
        {fees.length > 0 && (
          <>
            <div className="row g-3 mb-4">
              <div className="col-md-4">
                <div className="card bg-primary bg-opacity-10 border-primary">
                  <div className="card-body text-center">
                    <h4 className="text-primary mb-1">₹{stats.totalAmount.toLocaleString('en-IN')}</h4>
                    <p className="mb-0 text-muted small">Total Fee Amount</p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card bg-success bg-opacity-10 border-success">
                  <div className="card-body text-center">
                    <h4 className="text-success mb-1">₹{stats.totalPaid.toLocaleString('en-IN')}</h4>
                    <p className="mb-0 text-muted small">Total Paid</p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card bg-danger bg-opacity-10 border-danger">
                  <div className="card-body text-center">
                    <h4 className="text-danger mb-1">₹{stats.totalDue.toLocaleString('en-IN')}</h4>
                    <p className="mb-0 text-muted small">Total Outstanding</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Fee Details Table */}
            <h6 className="mb-3">
              <i className="bi bi-list-ul me-2"></i>
              Fee Breakdown
            </h6>
            <div className="table-responsive">
              <table className="table table-hover table-bordered">
                <thead className="table-light">
                  <tr>
                    <th>Term / Installment</th>
                    <th className="text-end">Total Amount</th>
                    <th className="text-end">Paid</th>
                    <th className="text-end">Balance Due</th>
                    <th>Due Date</th>
                    <th className="text-center">Status</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {fees.map((fee) => (
                    <tr key={fee.id}>
                      <td>
                        <strong>{fee.term}</strong>
                      </td>
                      <td className="text-end">₹{fee.amount.toLocaleString('en-IN')}</td>
                      <td className="text-end text-success">₹{fee.paid.toLocaleString('en-IN')}</td>
                      <td className="text-end text-danger">
                        <strong>₹{fee.due.toLocaleString('en-IN')}</strong>
                      </td>
                      <td>
                        {new Date(fee.dueDate).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </td>
                      <td className="text-center">
                        <span className={`badge ${
                          fee.status === 'paid' ? 'bg-success' :
                          fee.status === 'pending' ? 'bg-warning text-dark' : 'bg-danger'
                        }`}>
                          {fee.status === 'paid' ? (
                            <><i className="bi bi-check-circle me-1"></i>PAID</>
                          ) : fee.status === 'pending' ? (
                            <><i className="bi bi-clock me-1"></i>PENDING</>
                          ) : (
                            <><i className="bi bi-exclamation-circle me-1"></i>OVERDUE</>
                          )}
                        </span>
                      </td>
                      <td className="text-center">
                        {fee.status !== 'paid' ? (
                          <button className="btn btn-sm btn-primary">
                            <i className="bi bi-credit-card me-1"></i>
                            Pay Now
                          </button>
                        ) : (
                          <button className="btn btn-sm btn-outline-secondary" disabled>
                            <i className="bi bi-check-circle me-1"></i>
                            Paid
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="table-light">
                  <tr>
                    <th>Total</th>
                    <th className="text-end">₹{stats.totalAmount.toLocaleString('en-IN')}</th>
                    <th className="text-end text-success">₹{stats.totalPaid.toLocaleString('en-IN')}</th>
                    <th className="text-end text-danger">₹{stats.totalDue.toLocaleString('en-IN')}</th>
                    <th colSpan={3}></th>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Actions */}
            <div className="mt-3 d-flex gap-2">
              <button className="btn btn-outline-primary">
                <i className="bi bi-printer me-2"></i>
                Print Fee Receipt
              </button>
              <button className="btn btn-outline-success">
                <i className="bi bi-download me-2"></i>
                Download Statement
              </button>
            </div>
          </>
        )}

        {/* No Data Message */}
        {!loading && fees.length === 0 && studentId && studentName && (
          <div className="text-center py-5">
            <i className="bi bi-inbox display-1 text-muted"></i>
            <p className="mt-3 text-muted">No fee records found for this student.</p>
          </div>
        )}
      </div>

    </div>
  );
};

export default StudentFeesLookup;
